/*
 *  Copyright 2020 Adobe
 *
 *  Licensed under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License.
 *  You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software
 *  distributed under the License is distributed on an "AS IS" BASIS,
 *  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *  See the License for the specific language governing permissions and
 *  limitations under the License.
 */

import * as React from 'react';

import { ComponentMapping, Container } from '@adobe/aem-react-editable-components';
import { withStandardBaseCssClass } from '@adobe/aem-core-components-react-base';
import {
  CoreContainerProperties,
  CoreContainerState,
  withAuthorPanelSwitch,
} from '@adobe/aem-core-components-react-spa';
import { Model } from '@adobe/aem-spa-page-model-manager';
import { MenuItem, Select } from '@mui/material';
import styles from './SectionList.module.scss';
import { useDevice } from '../../../../lib/hooks/device';
import { DeviceType } from '../../../../lib/utils/getDevice';
import { classes } from '../../../../lib/utils/cssUtils';

export interface SectionListProperties extends CoreContainerProperties {
  device: DeviceType;
  expandedItems: string[];
}

export interface SectionListState extends CoreContainerState {
  sectionRefs: Record<string, React.RefObject<HTMLHeadingElement>>;
  selectedSection: string;
  isNewSelection: boolean;
  expandedItems: string[];
}

type SectionItem = Model & {
  'cq:panelTitle': string;
};

const withDeviceHook =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: SectionListProperties & T) => {
    const device = useDevice();

    return <Component {...props} device={device} />;
  };

export const SectionListIsEmptyFn = (props: SectionListProperties) =>
  props.cqItemsOrder == null || props.cqItemsOrder.length === 0;

class SectionList extends Container<SectionListProperties, SectionListState> {
  constructor(props: SectionListProperties) {
    super(props);

    this.state = {
      componentMapping: this.props.componentMapping || ComponentMapping,
      sectionRefs: this.props.cqItemsOrder.reduce(
        (acc, item) => ({ ...acc, [item]: React.createRef<HTMLHeadingElement>() }),
        {},
      ),
      selectedSection: this.props.cqItemsOrder[0],
      isNewSelection: false,
      expandedItems: this.props.expandedItems,
    };
  }

  setSelectedSection(sectionId: string) {
    this.setState({ selectedSection: sectionId, isNewSelection: true });
  }

  get accordionContainerProps() {
    const attrs = this.containerProps;
    attrs.className = `${attrs.className} ${this.props.baseCssClass}`;
    attrs['data-cmp-is'] = 'accordion';

    return attrs;
  }

  displayItem(sectionId: string) {
    const indexToShow = this.props.cqItemsOrder.indexOf(sectionId);

    return <div role="region">{this.childComponents[indexToShow]}</div>;
  }

  isItemExpanded(key: string) {
    return this.state.expandedItems.indexOf(key) > -1;
  }

  focusWithScrollSection(sectionId: string) {
    if (sectionId && this.state.sectionRefs[sectionId] !== null) {
      this.state.sectionRefs[sectionId].current?.focus({ preventScroll: true });
      this.state.sectionRefs[sectionId].current?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  handleSectionChangeDesktop(sectionId: string) {
    if (sectionId && this.state.sectionRefs[sectionId] !== null) {
      this.setState({ selectedSection: sectionId });
      this.focusWithScrollSection(sectionId);
    }
  }

  handleFocus(event: React.FocusEvent<HTMLDivElement, Element>) {
    const currentSection = this.state.selectedSection;
    if (this.state.sectionRefs[currentSection] && this.state.isNewSelection) {
      event.preventDefault();
      event.stopPropagation();
      this.focusWithScrollSection(currentSection);
    }
    this.setState({ isNewSelection: false });
  }

  get sectionListContent() {
    return this.props.cqItemsOrder.map((sectionId, index) => {
      const item = this.props.cqItems[sectionId] as SectionItem;
      const sectionName = item['cq:panelTitle'];

      return (
        <section
          key={`sectionlist-section-${sectionId}`}
          className={styles.sectionItemContainer}
          data-cmp-index={index}
          data-cmp-expanded
        >
          <div className={styles.sectionItem}>
            <div className={styles.itemContainer}>
              <h2 className={styles.heading} ref={this.state.sectionRefs[sectionId]} tabIndex={-1}>
                {sectionName}
              </h2>
              <div className={styles.itemContainerSpacing} />
              <div className={styles.contentWrapper}>{this.displayItem(sectionId)}</div>
            </div>
          </div>
        </section>
      );
    });
  }

  idToSectionName(sectionId: string) {
    return (this.props.cqItems[sectionId] as SectionItem)?.['cq:panelTitle'] ?? sectionId;
  }

  get sectionListNav() {
    const { device } = this.props;

    return (
      <div className={styles.navigationContainer}>
        <nav className={styles.navigation}>
          {device === 'desktop' ? (
            <ul className={styles.navigationList}>
              {this.props.cqItemsOrder.map(sectionId => {
                const item = this.props.cqItems[sectionId] as SectionItem;
                const sectionName = item['cq:panelTitle'];
                const buttonClass = classes(styles.navigationItemLink, {
                  [styles.selectedItem]: sectionId === this.state.selectedSection,
                });

                return (
                  <li className={styles.navigationItem} key={`${sectionName}-${sectionId}`}>
                    <button
                      title={sectionName}
                      className={buttonClass}
                      onClick={() => this.handleSectionChangeDesktop(sectionId)}
                    >
                      <span className={styles.itemLinkText}>{sectionName}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <Select
              value={this.state.selectedSection}
              variant="standard"
              classes={{ root: styles.selectRoot, select: styles.selectButton, icon: styles.selectIcon }}
              MenuProps={{
                classes: { paper: styles.selectMenuPaper, list: styles.selectMenuList, root: styles.selectMenuRoot },
                slotProps: { paper: { square: true, elevation: 0 } },
                disableScrollLock: true,
              }}
              renderValue={(val: string) => (
                <span className={styles.selectButtonText}>{this.idToSectionName(val)}</span>
              )}
              onChange={event => this.setSelectedSection(event.target.value)}
              onFocusCapture={event => this.handleFocus(event)}
            >
              {this.props.cqItemsOrder?.map(sectionId => (
                <MenuItem
                  value={sectionId}
                  key={`section-menu-${sectionId}`}
                  classes={{
                    root: classes(styles.selectMenuItem, styles.disableBackground),
                    focusVisible: classes(styles.selectedItem, styles.disableBackground),
                    selected: styles.disableBackground,
                  }}
                >
                  <span className={styles.itemLinkText}>{this.idToSectionName(sectionId)}</span>
                </MenuItem>
              ))}
            </Select>
          )}
        </nav>
      </div>
    );
  }

  render() {
    const isEmpty = SectionListIsEmptyFn(this.props);

    return (
      <div
        {...this.accordionContainerProps}
        className={`${this.accordionContainerProps.className} ${styles.sectionList}`}
      >
        {!isEmpty && this.sectionListNav}
        {!isEmpty && this.sectionListContent}
        {this.placeholderComponent}
      </div>
    );
  }
}

export default withStandardBaseCssClass(withAuthorPanelSwitch(withDeviceHook(SectionList)), 'cmp-accordion');
